/* eslint-disable max-len */
import React, {
    useCallback, useState, useEffect, useContext
} from 'react';
import _ from 'lodash';
import { TranslatorContext, IntlContext } from '@jutro/locale';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { useValidation } from 'gw-portals-validation-react';
import { withRouter } from 'react-router-dom';
// eslint-disable-next-line import/no-unresolved
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import {
    withApplicationPropertyContext
} from 'tdic-components-platform-react';
import { TermsAndConditionUtils } from 'gw-portals-util-js';
import metadata from './ProfileChangePage.metadata.json5';
import messages from './ProfileChangePage.messages';
import commonMessages from '../../PolicyChangeCommon.messages';
import styles from '../../PolicyChange.module.scss';

function ProfileChangePage(props) {
    const translator = useContext(TranslatorContext);
    const {
        wizardData, updateWizardData, authHeader, tncTransactionTypesSubTypes,
        user_name: userName, email, termData: terms, history
    } = props;
    const { submissionVM } = wizardData;
    const { EndorsementService } = useDependencies('EndorsementService');
    const [prefixValue, setPrefixValue] = useState(false);
    const [isCompanyAccount, setCompanyAccount] = useState(false);
    const { onValidate, initialValidation, isComponentValid } = useValidation(
        'ProfileChangePage'
    );
    const [isAcknowledged, setAcknowledgement] = useState(false);
    const [previousPrimaryNamedInsured, setPreviousPrimaryNamedInsured] = useState(null);
    if (previousPrimaryNamedInsured === null) {
        const pniValue = _.get(submissionVM.value, 'baseData_Ext.primaryNamedInsured', null);
        setPreviousPrimaryNamedInsured(_.cloneDeep(pniValue));
    }

    const intl = useContext(IntlContext);
    const getFormattedDate = useCallback((item) => {
        return intl.formatDate(new Date(item), { year: 'numeric', month: 'short', day: 'numeric' });
    }, [intl]);

    useEffect(() => {
        // Direct entry guard
        if (!submissionVM || !submissionVM.value) {
            history.replace('/profile-change');
        }
    // eslint-disable-next-line react/destructuring-assignment
    }, [history, submissionVM]);

    useEffect(() => {
        if (!_.isUndefined(_.get(submissionVM.value, 'baseData_Ext.primaryNamedInsured.prefix'))) {
            setPrefixValue(true);
        } else {
            _.set(submissionVM.value, 'baseData_Ext.primaryNamedInsured.prefix', 'dr');
        }
        const firstName = _.get(submissionVM.value, 'baseData_Ext.primaryNamedInsured.firstName', '');
        if (_.isEmpty(firstName)) {
            setCompanyAccount(true);
        }
        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onNext = useCallback(async () => {
        const requestPayload = _.omit(submissionVM.value, 'lobData');
        submissionVM.value = await EndorsementService.saveEndorsement(
            [requestPayload],
            authHeader
        );
        TermsAndConditionUtils.saveTnCAcceptance(_.get(terms, 'Version', ''), tncTransactionTypesSubTypes, 'PolicyChange', 'profileChange', authHeader, _.get(submissionVM.value, 'jobID'), userName, email);
        setTimeout(() => {
            window.location.reload();
        }, 1500); // 1.5 seconds delay

        return wizardData;
    }, [submissionVM, EndorsementService, authHeader, terms, tncTransactionTypesSubTypes,
        userName, email, wizardData]);

    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            showOptional: true
        },
        termsAndConditionsCheckbox: {
            value: isAcknowledged,
            onValueChange: setAcknowledgement,
            label: commonMessages.termsAndConditionsCheckbox
        },
        firstName: {
            visible: !isCompanyAccount,
            value: _.get(submissionVM, 'baseData_Ext.primaryNamedInsured.firstName.value')
        },
        lastName: {
            visible: !isCompanyAccount,
            value: _.get(submissionVM, 'baseData_Ext.primaryNamedInsured.lastName.value')
        },
        prefix: {
            required: !prefixValue,
            visible: !isCompanyAccount
        },
        nameField: {
            visible: isCompanyAccount,
            value: _.get(submissionVM, 'baseData_Ext.primaryNamedInsured.name.value')
        },
        suffix: {
            visible: !isCompanyAccount,
            value: translator({
                id: _.get(submissionVM, 'baseData_Ext.primaryNamedInsured.suffix.value.name'),
                defaultMessage: ''
            })
        },
        dateOfBirth: {
            visible: !isCompanyAccount,
            value: getFormattedDate(new Date(
                _.get(submissionVM, 'baseData_Ext.primaryNamedInsured.dateOfBirth.value.year'),
                _.get(submissionVM, 'baseData_Ext.primaryNamedInsured.dateOfBirth.value.month'),
                _.get(submissionVM, 'baseData_Ext.primaryNamedInsured.dateOfBirth.value.day')
            ))
        },
        mobilePhone: {
            visible: !isCompanyAccount
        },
        workPhone: {
            label: !isCompanyAccount ? messages.workPhone
                : messages.officePhone
        },
        homePhone: {
            visible: !isCompanyAccount
        },
        primaryPhone: {
            visible: !isCompanyAccount
        },
        officePhone: {
            visible: isCompanyAccount
        }
    };

    const unFormatPhoneNumber = useCallback((phoneNumber) => {
        if (phoneNumber && phoneNumber !== '') {
            return phoneNumber.replace(/\D/g, '');
        }
        return undefined;
    }, []);

    const isPhoneNumberChanged = useCallback((newValue, oldValue) => {
        if (unFormatPhoneNumber(newValue) !== unFormatPhoneNumber(oldValue)) {
            return true;
        }
        return false;
    }, [unFormatPhoneNumber]);

    const isCompanyPNIChanged = useCallback((primaryNamedInsured) => {
        if (primaryNamedInsured.emailAddress !== previousPrimaryNamedInsured.emailAddress) {
            return true;
        }
        if (isPhoneNumberChanged(primaryNamedInsured.workPhone,
            previousPrimaryNamedInsured.workPhone)) {
            return true;
        }
        if (isPhoneNumberChanged(primaryNamedInsured.faxPhone,
            previousPrimaryNamedInsured.faxPhone)) {
            return true;
        }
        return false;
    }, [previousPrimaryNamedInsured, isPhoneNumberChanged]);

    const isPersonPNIChanged = useCallback((primaryNamedInsured) => {
        if (primaryNamedInsured.prefix !== previousPrimaryNamedInsured.prefix) {
            return true;
        }
        if (primaryNamedInsured.primaryPhoneType !== previousPrimaryNamedInsured.primaryPhoneType) {
            return true;
        }
        if (primaryNamedInsured.emailAddress !== previousPrimaryNamedInsured.emailAddress) {
            return true;
        }
        if (isPhoneNumberChanged(primaryNamedInsured.workPhone,
            previousPrimaryNamedInsured.workPhone)) {
            return true;
        }
        if (isPhoneNumberChanged(primaryNamedInsured.homePhone,
            previousPrimaryNamedInsured.homePhone)) {
            return true;
        }
        if (isPhoneNumberChanged(primaryNamedInsured.mobilePhone,
            previousPrimaryNamedInsured.mobilePhone)) {
            return true;
        }
        if (isPhoneNumberChanged(primaryNamedInsured.faxPhone,
            previousPrimaryNamedInsured.faxPhone)) {
            return true;
        }
        return false;
    }, [previousPrimaryNamedInsured, isPhoneNumberChanged]);

    const isFormvalid = useCallback(() => {
        const primaryNamedInsured = _.get(submissionVM.value, 'baseData_Ext.primaryNamedInsured', null);
        if (isCompanyAccount) {
            return (!isComponentValid || !isAcknowledged
                || !isCompanyPNIChanged(primaryNamedInsured));
        }
        return (!isComponentValid || !isAcknowledged
                || !isPersonPNIChanged(primaryNamedInsured));
    }, [isComponentValid, isAcknowledged, submissionVM, isCompanyAccount,
        isCompanyPNIChanged, isPersonPNIChanged]);

    const resolvers = {
        resolveClassNameMap: styles
    };

    return (
        <WizardPage
            onNext={onNext}
            cancelLabel={commonMessages.cancelAllChanges}
            disableNext={isFormvalid()}
            skipWhen={initialValidation}
            nextLabel={messages.submit}
            previousLabel={messages.back}
            showPrevious
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={wizardData}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}

ProfileChangePage.propTypes = wizardProps;
export default withAuthenticationContext(withApplicationPropertyContext(withRouter(ProfileChangePage)));
